import { c as _c } from "react/compiler-runtime";
import { getEntitiesByFpIdDataOptions, getEntitiesByFpIdOnboardingsByOnboardingIdRiskSignalsOptions, getEntitiesByFpIdOnboardingsByOnboardingIdTraceOptions } from '@onefootprint/fetch-dashboard';
import { useIntl } from '@onefootprint/hooks';
import { IcoMaximize16 } from '@onefootprint/icons';
import type { EntityOnboarding } from '@onefootprint/request-types/dashboard';
import { Card, LinkButton } from '@onefootprint/ui';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BUSINESS_INSIGHT_SUBSECTIONS, Decrypt, DocumentsDecrypt, EncryptedBusinessInsights, getRelevantDis, groupRiskSignals, ObeRules, OnboardingBusinessInsight, OnboardingDocuments, OnboardingFieldValidations, OnboardingRiskSignals, OnboardingRules, OnboardingVaultData, type SubsectionType as Subsection, Subsection as SubsectionBody } from '@/components/entity-onboarding-details';
import useEntityVault from '@/entities/hooks/use-entity-vault';
import type { WithEntityProps } from '@/entity/components/with-entity';
import useEntityKind from '@/entity/hooks/use-entity-kind';
import { useOpenPage } from '@/hooks/use-open-page';
import DeviceInsights from '../../../device-insights';
import DuplicateData from '../../../duplicate-data';
import OtherInsights from '../../../other-insights';
import { CURRENT_ONBOARDING_ID, getSubsectionAnchorId } from '../../constants';
import useShowOnboarding from '../../hooks/use-show-onboarding';
import OnboardingMoreInfo from './components/onboarding-more-info';
import useEmptySubsections from './hooks/use-empty-subsections';
import useSubsections from './hooks/use-subsections';
type OnboardingDataProps = WithEntityProps & {
  onboarding: EntityOnboarding;
};
type SubsectionCard = {
  key: string;
  title: string;
  actions?: ReactNode;
  currentLink?: ReactNode;
  /** Content that brings its own border, like a table, sits on the card ground without a body. */
  isBare?: boolean;
  content: ReactNode;
};
const isBusinessInsight = (key: Subsection) => (BUSINESS_INSIGHT_SUBSECTIONS as readonly Subsection[]).includes(key);
const OnboardingData = t0 => {
  const $ = _c(62);
  const {
    entity,
    onboarding
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "onboardings"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const {
    formatDateWithTime
  } = useIntl();
  const entityKind = useEntityKind();
  const handleOpenPage = useOpenPage();
  const {
    scrollOnboardingIntoView
  } = useShowOnboarding();
  let t2;
  if ($[1] !== entity.id || $[2] !== onboarding.id) {
    t2 = getEntitiesByFpIdOnboardingsByOnboardingIdRiskSignalsOptions({
      path: {
        fpId: entity.id,
        onboardingId: onboarding.id
      }
    });
    $[1] = entity.id;
    $[2] = onboarding.id;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = Boolean(entity.id) && Boolean(onboarding.id);
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = {
      ...t2,
      enabled: t3
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    data: riskSignals,
    isPending: isRiskSignalsPending
  } = useQuery(t4);
  let t5;
  if ($[7] !== entity.id || $[8] !== onboarding.seqno) {
    t5 = getEntitiesByFpIdDataOptions({
      path: {
        fpId: entity.id
      },
      query: {
        seqno: onboarding.seqno
      }
    });
    $[7] = entity.id;
    $[8] = onboarding.seqno;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = Boolean(entity.id);
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = {
      ...t5,
      enabled: t6
    };
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const {
    data: entityAttributes
  } = useQuery(t7);
  let t8;
  if ($[13] !== entity.id || $[14] !== onboarding.id) {
    t8 = getEntitiesByFpIdOnboardingsByOnboardingIdTraceOptions({
      path: {
        fpId: entity.id,
        onboardingId: onboarding.id
      }
    });
    $[13] = entity.id;
    $[14] = onboarding.id;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== onboarding.hasGraphTrace || $[17] !== t8) {
    t9 = {
      ...t8,
      enabled: onboarding.hasGraphTrace
    };
    $[16] = onboarding.hasGraphTrace;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const {
    data: onboardingTrace
  } = useQuery(t9);
  let t10;
  if ($[19] !== entity.id || $[20] !== entityAttributes || $[21] !== onboarding.seqno) {
    t10 = {
      entityAttributes,
      entityId: entity.id,
      seqno: onboarding.seqno
    };
    $[19] = entity.id;
    $[20] = entityAttributes;
    $[21] = onboarding.seqno;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const entityVault = useEntityVault(t10);
  const subsections = useSubsections(onboarding);
  const isEmpty = useEmptySubsections(entity, onboarding);
  let t11;
  if ($[23] !== entityAttributes) {
    t11 = entityAttributes?.some(_temp);
    $[23] = entityAttributes;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const hasDecryptableDIs = Boolean(t11);
  let t12;
  if ($[25] !== entityAttributes || $[26] !== entityVault || $[27] !== hasDecryptableDIs) {
    t12 = hasDecryptableDIs && getRelevantDis((entityAttributes ?? []).filter(_temp2).map(_temp3)).every(di => entityVault.isDecrypted(di));
    $[25] = entityAttributes;
    $[26] = entityVault;
    $[27] = hasDecryptableDIs;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const isVaultDecrypted = t12;
  const showVaultDecrypt = hasDecryptableDIs ? !isVaultDecrypted : Boolean(entityAttributes?.length);
  let t13;
  let t14;
  if ($[29] !== entity || $[30] !== entityKind?.urlPath || $[31] !== entityVault || $[32] !== formatDateWithTime || $[33] !== handleOpenPage || $[34] !== hasDecryptableDIs || $[35] !== isEmpty || $[36] !== isRiskSignalsPending || $[37] !== isVaultDecrypted || $[38] !== onboarding || $[39] !== onboardingTrace || $[40] !== riskSignals || $[41] !== scrollOnboardingIntoView || $[42] !== showVaultDecrypt || $[43] !== subsections || $[44] !== t) {
    const asOfText = onboarding.completedAt ? t(_temp4, {
      date: formatDateWithTime(new Date(onboarding.completedAt))
    }) : t(_temp5);
    let t15;
    if ($[47] !== scrollOnboardingIntoView || $[48] !== t) {
      t15 = key => <LinkButton onClick={() => scrollOnboardingIntoView(CURRENT_ONBOARDING_ID, key)}>{t(_temp6)}</LinkButton>;
      $[47] = scrollOnboardingIntoView;
      $[48] = t;
      $[49] = t15;
    } else {
      t15 = $[49];
    }
    const getCurrentLink = t15;
    let t16;
    if ($[50] !== entity || $[51] !== entityVault.data || $[52] !== entityVault.update || $[53] !== hasDecryptableDIs || $[54] !== onboarding) {
      t16 = <Decrypt canDecrypt={hasDecryptableDIs} onDecryptSuccess={entityVault.update} onboarding={onboarding} vaultData={entityVault.data} entity={entity} />;
      $[50] = entity;
      $[51] = entityVault.data;
      $[52] = entityVault.update;
      $[53] = hasDecryptableDIs;
      $[54] = onboarding;
      $[55] = t16;
    } else {
      t16 = $[55];
    }
    const decryptAction = t16;
    const getCard = (key_0, title) => {
      switch (key_0) {
        case "more-information":
          {
            return {
              key: key_0,
              title,
              actions: onboarding.hasGraphTrace ? <LinkButton iconComponent={IcoMaximize16} iconPosition="left" onClick={event => handleOpenPage(event, `/${entityKind?.urlPath}/${entity.id}/onboardings/${onboarding.id}/trace`)}>{t(_temp7)}</LinkButton> : undefined,
              content: <OnboardingMoreInfo onboarding={onboarding} />
            };
          }
        case "rules":
          {
            if (!onboarding.hasGraphTrace) {
              return {
                key: key_0,
                title,
                content: <OnboardingRules ruleSetResults={onboarding.ruleSetResults} />
              };
            }
            return {
              key: key_0,
              title,
              content: onboardingTrace ? <ObeRules trace={onboardingTrace} /> : null
            };
          }
        case "risk-signals":
          {
            return {
              key: key_0,
              title,
              content: <OnboardingRiskSignals isLoading={isRiskSignalsPending} riskSignals={groupRiskSignals(riskSignals ?? [])} />
            };
          }
        case "user-data":
        case "business-data":
          {
            return {
              key: key_0,
              title,
              actions: showVaultDecrypt ? decryptAction : null,
              content: <OnboardingVaultData entity={entity} vault={entityVault} />
            };
          }
        case "field-validations":
          {
            return {
              key: key_0,
              title,
              content: <SubsectionBody><OnboardingFieldValidations entityId={entity.id} onboardingId={onboarding.id} /></SubsectionBody>
            };
          }
        case "documents":
          {
            return {
              key: key_0,
              title,
              actions: <DocumentsDecrypt entity={entity} onboarding={onboarding} />,
              content: <OnboardingDocuments entity={entity} onboarding={onboarding} />
            };
          }
        case "duplicate-data":
          {
            return {
              key: key_0,
              title,
              actions: asOfText,
              currentLink: onboarding.completedAt ? getCurrentLink(key_0) : undefined,
              content: <DuplicateData timestamp={onboarding.completedAt} />
            };
          }
        case "device-insights":
          {
            return {
              key: key_0,
              title,
              actions: asOfText,
              currentLink: onboarding.completedAt ? getCurrentLink(key_0) : undefined,
              isBare: true,
              content: <DeviceInsights asOf={onboarding.completedAt} />
            };
          }
        case "other-insights":
          {
            return {
              key: key_0,
              title,
              currentLink: getCurrentLink(key_0),
              content: <SubsectionBody><OtherInsights onboardingId={onboarding.id} /></SubsectionBody>
            };
          }
        default:
          {
            return {
              key: key_0,
              title,
              content: <OnboardingBusinessInsight selectedSubsection={key_0} onboarding={onboarding} />
            };
          }
      }
    };
    const cards = subsections.flatMap(t17 => {
      const {
        key: key_1,
        title: title_0
      } = t17;
      if (!isBusinessInsight(key_1) || isVaultDecrypted) {
        return [getCard(key_1, title_0)];
      }
      if (key_1 !== BUSINESS_INSIGHT_SUBSECTIONS[0]) {
        return [];
      }
      return [{
        key: "business-insights",
        title: t(_temp8),
        content: <EncryptedBusinessInsights canDecrypt={hasDecryptableDIs} entity={entity} onboarding={onboarding} onDecryptSuccess={entityVault.update} vaultData={entityVault.data} />
      }];
    });
    t13 = "flex flex-col gap-4";
    let t18;
    if ($[56] !== isEmpty || $[57] !== t) {
      t18 = t19 => {
        const {
          key: key_2,
          title: title_1,
          actions,
          currentLink,
          isBare,
          content
        } = t19;
        const isCardEmpty = isEmpty[key_2 as Subsection];
        const label = isCardEmpty ? t(_temp9) : actions;
        return <Card.Root key={key_2} id={getSubsectionAnchorId(key_2)}><Card.Header title={title_1} rightContent={currentLink ? <div className="flex flex-wrap items-center justify-end gap-x-3 text-body-2 text-tertiary">{label}{currentLink}</div> : label} />{!isCardEmpty && (isBare ? content : <Card.Body className="overflow-hidden">{content}</Card.Body>)}</Card.Root>;
      };
      $[56] = isEmpty;
      $[57] = t;
      $[58] = t18;
    } else {
      t18 = $[58];
    }
    t14 = cards.map(t18);
    $[29] = entity;
    $[30] = entityKind?.urlPath;
    $[31] = entityVault;
    $[32] = formatDateWithTime;
    $[33] = handleOpenPage;
    $[34] = hasDecryptableDIs;
    $[35] = isEmpty;
    $[36] = isRiskSignalsPending;
    $[37] = isVaultDecrypted;
    $[38] = onboarding;
    $[39] = onboardingTrace;
    $[40] = riskSignals;
    $[41] = scrollOnboardingIntoView;
    $[42] = showVaultDecrypt;
    $[43] = subsections;
    $[44] = t;
    $[45] = t13;
    $[46] = t14;
  } else {
    t13 = $[45];
    t14 = $[46];
  }
  let t15;
  if ($[59] !== t13 || $[60] !== t14) {
    t15 = <div className={t13}>{t14}</div>;
    $[59] = t13;
    $[60] = t14;
    $[61] = t15;
  } else {
    t15 = $[61];
  }
  return t15;
};
export default OnboardingData;
function _temp(attr) {
  return attr.isDecryptable;
}
function _temp2(attr_0) {
  return attr_0.isDecryptable;
}
function _temp3(attr_1) {
  return attr_1.identifier;
}
function _temp4($) {
  return $.asOf;
}
function _temp5($_0) {
  return $_0.current;
}
function _temp6($_1) {
  return $_1.viewCurrent;
}
function _temp7($_2) {
  return $_2.onboardingTrace.showExpandedView;
}
function _temp8($_3) {
  return $_3.businessShared.title;
}
function _temp9($_4) {
  return $_4.none;
}